import { Calendar, Eye, Hand } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Column } from "@/components/DataTable";
import { Pill } from "@/components/ui/pill";
import { Dot } from "@/components/ui/dot";
import { Button } from "@/components/ui/button";
import { MarkingPendingPill } from "./MarkingPendingPill";
import {
  OP_STATUS_BLOCK,
  OP_STATUS_TONE,
  statusText,
  formatWindow,
  durationLabel,
  dueLabel,
  latenessCellValue,
  oeeCellValue,
  initials,
} from "./production-order-utils";
import type { ProductionOrder } from "../../types/production-orders.types.js";

function StatusTile({ o }: { o: ProductionOrder }) {
  const b = OP_STATUS_BLOCK[o.status];
  return (
    <span
      className={cn(
        "grid h-[30px] w-[30px] shrink-0 place-items-center rounded-sm border border-l-[3px]",
        b.bg,
        b.border,
        b.accent
      )}
    >
      <Calendar className={cn("h-3.5 w-3.5", b.text)} />
    </span>
  );
}

function Avatar({ name }: { name: string | null }) {
  return (
    <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-brand-100 text-[9px] font-semibold text-brand-700">
      {initials(name)}
    </span>
  );
}

// Atraso. Hierarquia deliberada: só o ATRASO grita (Pill danger), o "no prazo" é
// discreto (dot + rótulo). Uma tabela de PCP majoritariamente no prazo viraria um muro verde se
// os dois estados tivessem o mesmo peso — e aí o problema real deixaria de saltar aos olhos.
// Nunca só cor: os três estados carregam rótulo (e dot quando há compromisso).
function LatenessCell({ o }: { o: ProductionOrder }) {
  const { kind, text } = latenessCellValue(o);
  if (kind === "late")
    return (
      <Pill tone="danger" withDot>
        {text}
      </Pill>
    );
  if (kind === "onTime")
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-text-secondary">
        <Dot tone="success" size={6} halo={false} />
        {text}
      </span>
    );
  return <span className="text-text-disabled">{text}</span>;
}

// Render visível da coluna OEE — mesma regra do CSV (oeeCellValue), só adicionando a cor por kind.
function OeeCell({ o }: { o: ProductionOrder }) {
  const { kind, text } = oeeCellValue(o);
  if (kind === "completed")
    return <span className="font-mono font-medium tabular-nums">{text}</span>;
  if (kind === "running") return <span className="text-[11px] text-rd-success-700">{text}</span>;
  return <span className="text-text-disabled">{text}</span>;
}

// Colunas da lista de OPs. Linha/ação abrem a OP (roteado por status pelo index).
export function orderColumns(onOpen: (o: ProductionOrder) => void): Column<ProductionOrder>[] {
  return [
    {
      header: "Ordem",
      headerClassName: "w-[16%]",
      accessor: (o) => (
        <div className="flex items-center gap-2.5">
          <StatusTile o={o} />
          <div className="min-w-0">
            <div className="flex items-center gap-1 font-mono text-xs font-semibold">
              {o.code ?? "—"}
              {o.origin === "MANUAL" ? (
                // OP iniciada manualmente (vs agendada).
                <Hand
                  className="h-3 w-3 shrink-0 text-text-tertiary"
                  aria-label="Iniciada manualmente"
                />
              ) : null}
            </div>
            <div className="truncate text-[11px] text-text-tertiary">{o.lineName}</div>
          </div>
        </div>
      ),
    },
    {
      header: "Produto(s)",
      headerClassName: "w-[14%]",
      accessor: (o) => (
        <div className="flex flex-wrap items-center gap-1">
          {o.products.slice(0, 2).map((p) => (
            <Pill key={p.productId} tone="brand">
              {p.name}
            </Pill>
          ))}
          {o.products.length > 2 ? (
            <span className="text-[11px] text-text-tertiary">+{o.products.length - 2}</span>
          ) : null}
        </div>
      ),
    },
    {
      header: "Status",
      headerClassName: "w-[12%]",
      accessor: (o) => (
        <div className="flex flex-col items-start gap-1">
          <Pill tone={OP_STATUS_TONE[o.status]} withDot pulse={o.status === "RUNNING"}>
            {statusText(o)}
          </Pill>
          <MarkingPendingPill order={o} />
        </div>
      ),
    },
    {
      header: "Janela",
      headerClassName: "w-[12%]",
      accessor: (o) => (
        <div>
          <div className="font-mono text-xs tabular-nums">
            {formatWindow(o.plannedStartAt, o.expectedEnd)}
          </div>
          <div className="text-[11px] text-text-tertiary">
            {durationLabel(o.plannedStartAt, o.expectedEnd)}
          </div>
        </div>
      ),
    },
    {
      header: "Prazo",
      headerClassName: "w-[10%]",
      accessor: (o) => (
        <span
          className={cn(
            "font-mono text-xs tabular-nums",
            o.dueDate ? undefined : "text-text-disabled"
          )}
        >
          {dueLabel(o.dueDate)}
        </span>
      ),
    },
    {
      header: "Atraso",
      headerClassName: "w-[10%]",
      accessor: (o) => <LatenessCell o={o} />,
    },
    {
      header: "Qtd.",
      headerClassName: "w-[8%] text-right",
      className: "text-right font-mono tabular-nums",
      accessor: (o) => (
        <>
          {o.plannedQty} <span className="text-[11px] text-text-tertiary">un</span>
        </>
      ),
    },
    {
      header: "Responsável",
      headerClassName: "w-[10%]",
      accessor: (o) => (
        <div className="flex items-center gap-2">
          <Avatar name={o.responsible} />
          <span className="truncate text-xs">{o.responsible ?? "—"}</span>
        </div>
      ),
    },
    {
      header: "OEE",
      headerClassName: "w-[8%]",
      accessor: (o) => <OeeCell o={o} />,
    },
    {
      header: "",
      headerClassName: "w-[56px]",
      accessor: (o) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label="Abrir ordem"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(o);
          }}
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];
}
